import { useMemo } from "react";
import { TaskSelectionSummary } from "@common/types/task";
import { Picker } from "@design-system/Picker/Picker.lib";
import { PlusIcon } from "@icons";
import classNames from "classnames";
import styles from "../TaskDetail.module.scss";
import {
    TaskBlockingListItem,
    TaskBlockingListItemProps,
} from "./TaskBlockingListItem";

type TaskListItemComponent = React.ComponentType<
    Readonly<TaskBlockingListItemProps>
>;

interface TaskBlockingListProps {
    taskIds: string[];
    availableToAdd: TaskSelectionSummary[];
    onAdd?: (taskId: string) => void;
    onRemove?: (taskId: string) => void;
    addOpen: boolean;
    setAddOpen: (open: boolean) => void;
    goToTask: (taskId: string) => void;
    emptyLabel: string;
    ItemComponent?: TaskListItemComponent;
}

export const TaskBlockingList = ({
    taskIds,
    availableToAdd,
    onAdd,
    onRemove,
    addOpen,
    setAddOpen,
    goToTask,
    emptyLabel,
    ItemComponent = TaskBlockingListItem,
}: Readonly<TaskBlockingListProps>) => {
    const canEdit = onAdd !== undefined && onRemove !== undefined;
    const addItems = useMemo(
        () =>
            availableToAdd.map((taskSummary) => ({
                value: taskSummary.id,
                label: taskSummary.name,
            })),
        [availableToAdd],
    );

    return (
        <div
            className={classNames(styles.blockingTaskList, styles.detailValue, {
                [styles.empty]:
                    taskIds.length === 0 && availableToAdd.length === 0,
            })}
        >
            {taskIds.length === 0 && availableToAdd.length === 0 && (
                <span className="text-gray-500">{emptyLabel}</span>
            )}
            {taskIds.map((id) => (
                <ItemComponent
                    key={id}
                    taskId={id}
                    onNavigate={goToTask}
                    onRemove={onRemove}
                />
            ))}
            {canEdit && availableToAdd.length > 0 && (
                <Picker.Select
                    items={addItems}
                    searchable
                    searchPlaceholder="Search tasks..."
                    emptyLabel="No tasks found"
                    value={null}
                    open={addOpen}
                    onOpenChange={setAddOpen}
                    onValueChange={(taskId) => {
                        if (taskId) {
                            onAdd(taskId);
                            setAddOpen(false);
                        }
                    }}
                    chevron="none"
                    trigger={
                        <Picker.Button
                            variant="link"
                            size="sm"
                            className={styles.addButton}
                            leftIcon={<PlusIcon />}
                        >
                            Add
                        </Picker.Button>
                    }
                />
            )}
        </div>
    );
};
